import { Button, Flex } from 'antd';

import { User } from 'interfaces/users';
import { createGuestTeam, softDeleteGuestTeam, updateGuestTeamMembers } from 'slices/teamsActions';
import { useAppDispatch, useAppSelector } from 'utilities/hooks';
import { Invitation } from 'interfaces/invitations';
import { syncInvitations } from 'slices/invitationsActions';
import { useModal } from 'hooks/useModal';
import { ActionSharingCancelSave } from './ActionSharingCancelSave';

type SharingUserType = {
    members: Partial<User>[];
    invitations: Partial<Invitation>[];
    teamId?: string;
    entityId?: string;
    entity?: Partial<{
        id: string;
        type: string;
        uuid: string;
    }>;
};

export const ActionSharingMembers = ({
    close,
    onSubmit,
    role,
    entity,
    hasExistingTeam,
}: {
    close: () => void;
    onSubmit: () => void;
    role: string;
    entity: any;
    hasExistingTeam: boolean;
}) => {
    const dispatch = useAppDispatch();
    const { openModal } = useModal();

    const {
        loading: teamLoading,
        members: teamMembers,
        membersMap: teamMembersMap,
    } = useAppSelector((state) => state.team);
    const { invitations } = useAppSelector((state) => state.invitations);
    const { memberIdListSelected } = useAppSelector((state) => state.shareables);

    const entityId = entity?.type === 'category' ? entity?.uuid : entity?.id;

    const handleRemoveAccess = () => {
        openModal(
            'confirmationModal',
            {
                text: 'Are you sure want to stop sharing?',
            },
            {
                onConfirm: handleConfirmRemoveAccess,
            },
        );
    };

    const handleConfirmRemoveAccess = () => {
        dispatch(softDeleteGuestTeam(entityId))
            .unwrap()
            .then(() => {
                onSubmit();
                close();
            })
            .catch(() => {});
    };

    const handleSave = () => {
        const promises: any[] = [];

        const data: SharingUserType = {
            members: teamMembers || [],
            invitations: invitations || [],
        };

        data.members = data.members.concat(memberIdListSelected.map((memberId: string) => ({ id: memberId })));
        data.members = data.members.map((member) => ({
            id: member.id,
            role: teamMembersMap[member.id!]?.role || role,
        }));

        data.invitations = data.invitations.map((invitation) => ({
            id: invitation.id,
            email: invitation.email,
            phone: invitation.phone,
            team_role: invitation.team_role || role,
        }));

        data.teamId = entityId;
        data.entityId = entityId;

        if (hasExistingTeam && !data.members.length) {
            dispatch(softDeleteGuestTeam(entityId))
                .unwrap()
                .then(() => {
                    onSubmit();
                    close();
                })
                .catch(() => {});
            return;
        }

        if (hasExistingTeam) {
            promises.push(updateGuestTeamMembers);
        } else {
            data.entity = {
                id: entityId,
                type: entity?.type,
            };
            promises.push(createGuestTeam);
        }

        if (invitations?.length) {
            promises.push(syncInvitations);
        }

        promises.forEach((promise) => {
            dispatch(promise(data))
                .unwrap()
                .then(() => {
                    onSubmit();
                    close();
                });
        });
    };

    return (
        <Flex style={{ width: '100%' }}>
            {!!teamMembers?.length && (
                <Button color="danger" variant="outlined" onClick={handleRemoveAccess} loading={teamLoading}>
                    Stop Sharing
                </Button>
            )}
            <ActionSharingCancelSave close={close} handleSave={handleSave} />
        </Flex>
    );
};
